
import { Link } from "react-router-dom";
import { LinkCopyButton } from "./link-copy-button";
import { ShareIntentButton } from "./share-intent-button";
import { SendToTelegramButton } from "./send-to-telegram-button";
import { useTeamSharePrefs } from "@/hooks/use-team-share-prefs";
import { buildMvpShareText, buildRsvpShareText } from "@/utils/share-message";
import { withCacheBuster } from "@/utils/share-intents";
import { ogGameUrl, ogRsvpUrl } from "@/utils/share-host";
import { format } from "date-fns";
import { CalendarIcon, Clock, Users, ExternalLink, BarChart, Trash2, Copy, Check, Vote, Pin, PinOff, Pencil, MapPin, ClipboardCheck, ChevronRight } from "lucide-react";
import { KickoffCountdown, kickoffDate } from "./kickoff-countdown";
import { Switch } from "@/components/ui/switch";
import { NudgePendingButton } from "./nudge-pending-button";
import { Game } from "@/types";
import { GlassCard } from "@/components/ui/glass-card";
import { GlowEffect } from "@/components/ui/glow-effect";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getGameDeletionImpact, type GameDeletionImpact } from "@/utils/game-operations";
import { EditGameDialog } from "./edit-game-dialog";
import { useUserRole } from "@/hooks/use-user-role";
import { isBye, BYE_UPDATE } from "@/utils/bye";
import { isSkippedPoll } from "@/utils/skipped-poll";



interface RsvpData {
  going: number;
  notGoing: number;
  total: number;
}

interface GameCardProps {
  game: Game;
  voteCounts?: number;
  /** True once the caller's vote-count map is actually populated. An empty map
   *  mid-fetch reads as zero votes for every game, which would brand voted
   *  polls "skipped" — so the badge stays hidden until this is true. */
  voteCountsLoaded?: boolean;
  rsvpGoingCount?: number;
  rsvpData?: RsvpData;
  isPinned?: boolean;
  /** True for the single soonest upcoming match — shows the kick-off countdown */
  isNextMatch?: boolean;
  playersPerSide?: number | null;
  teamName?: string | null;
  onToggleVoting?: (gameId: string, isOpen: boolean) => void;
  onDeleteGame?: (gameId: string) => void;
  onPinGame?: (gameId: string) => void;
  onEditGame?: () => void;
}

export function GameCard({ 
  game, 
  voteCounts = 0,
  voteCountsLoaded = false,
  rsvpGoingCount,
  rsvpData,
  isPinned = false,
  isNextMatch = false,
  playersPerSide,
  teamName,
  onToggleVoting,
  onDeleteGame,
  onPinGame,
  onEditGame
}: GameCardProps) {
  const formattedDate = format(new Date(game.date), "MMMM d, yyyy");
  const kickoffAt = kickoffDate(game.date, game.match_time);
  const formattedKickoff = kickoffAt ? format(kickoffAt, "h:mm a") : null;
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [markingBye, setMarkingBye] = useState(false);
  const gameIsBye = isBye(game as any);
  // Fails closed: with no loaded counts we say nothing rather than call a
  // voted poll skipped.
  const pollSkipped = voteCountsLoaded && isSkippedPoll(game as any, voteCounts);
  const isCancelled = !gameIsBye && (game as any).status === "cancelled";
  const showPastKickoff =
    !gameIsBye &&
    !pollSkipped &&
    game.voting_open &&
    new Date(game.date) < new Date(new Date().setHours(0, 0, 0, 0));
  const hasHeaderBadge =
    gameIsBye || isCancelled || isPinned || pollSkipped || showPastKickoff;


  const handleMarkBye = async () => {
    setMarkingBye(true);
    try {
      const { error } = await supabase
        .from("teamvote_games")
        .update({ ...BYE_UPDATE, opponent: "Bye" })
        .eq("id", game.id);
      if (error) throw error;
      toast.success("Marked as a bye — no RSVPs needed");
      setDeleteDialogOpen(false);
      onEditGame?.();
    } catch (err) {
      console.error("Error marking bye:", err);
      toast.error("Couldn't mark as a bye");
    } finally {
      setMarkingBye(false);
    }
  };

  const [isDeleting, setIsDeleting] = useState(false);
  const [pollCopied, setPollCopied] = useState(false);
  const [rsvpCopied, setRsvpCopied] = useState(false);
  const [impact, setImpact] = useState<GameDeletionImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const { isAdmin } = useUserRole();
  const { prefs: sharePrefs } = useTeamSharePrefs(game.team_id);

  useEffect(() => {
    if (!deleteDialogOpen) return;
    let cancelled = false;
    setImpact(null);
    setImpactLoading(true);
    getGameDeletionImpact(game.id)
      .then((res) => { if (!cancelled) setImpact(res); })
      .catch(() => { if (!cancelled) setImpact({ votes: 0, feedback: 0, rsvps: 0 }); })
      .finally(() => { if (!cancelled) setImpactLoading(false); });
    return () => { cancelled = true; };
  }, [deleteDialogOpen, game.id]);

  const handleToggle = () => {
    onToggleVoting?.(game.id, !game.voting_open);
  };
  
  const handleDelete = () => {
    if (onDeleteGame) {
      setIsDeleting(true);
      try {
        onDeleteGame(game.id);
      } finally {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
      }
    }
  };
  
  const handleCopyPollUrl = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    const pollUrl = withCacheBuster(ogGameUrl(game.id));
    navigator.clipboard.writeText(pollUrl)
      .then(() => {
        setPollCopied(true);
        toast.success("MVP Vote link copied");
        setTimeout(() => setPollCopied(false), 2000);
      })
      .catch(err => {
        console.error("Failed to copy poll URL:", err);
        toast.error("Failed to copy link");
      });
  };

  const handleCopyRsvpUrl = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    const ogRsvpUrlStr = withCacheBuster(ogRsvpUrl(game.id));
    navigator.clipboard.writeText(ogRsvpUrlStr)
      .then(() => {
        setRsvpCopied(true);
        toast.success("RSVP link copied");
        setTimeout(() => setRsvpCopied(false), 2000);
      })
      .catch(err => {
        console.error("Failed to copy RSVP URL:", err);
        toast.error("Failed to copy link");
      });
  };

  return (
    <GlassCard className="h-full flex flex-col relative overflow-hidden">
      {game.voting_open && (
        <GlowEffect
          mode="rotate"
          blur="soft"
          scale={1.05}
          duration={8}
          colors={[
            'hsl(196, 80%, 43%)',
            'hsl(259, 94%, 61%)',
            'hsl(199, 89%, 48%)',
            'hsl(212, 96%, 62%)',
          ]}
          className="opacity-20"
        />
      )}
      <div className="relative z-10 flex justify-between items-start mb-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground text-sm">
          <span className="flex items-center">
            <CalendarIcon className="h-4 w-4 me-1" />
            {formattedDate}
          </span>
          {!gameIsBye && formattedKickoff && (
            <span className="flex items-center">
              <Clock className="h-3.5 w-3.5 me-1" />
              {formattedKickoff}
            </span>
          )}
          {isNextMatch && !gameIsBye && (game as any).status !== "cancelled" && (
            <KickoffCountdown date={game.date} matchTime={game.match_time} />
          )}
        </div>

        
        <div className="flex flex-col items-end gap-1.5">
          {hasHeaderBadge && (
            <div className="flex flex-wrap items-center justify-end gap-1.5">
            {gameIsBye && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 border-muted-foreground/40 text-muted-foreground"
                title="No game this round — nobody is asked to RSVP"
              >
                Bye
              </Badge>
            )}
            {isCancelled && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 border-destructive/60 text-destructive"
                title={(game as any).cancellation_reason || "Cancelled"}
              >
                Cancelled
              </Badge>
            )}
            {isPinned && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/50 text-primary">
                <Pin className="h-3 w-3 me-0.5" /> Pinned
              </Badge>
            )}
            {/* Stated plainly, on its own line above the toggle: skipping the
                vote is a normal coaching choice (a gala day with one vote round
                at the end), so the badge explains the empty poll rather than
                leaving it looking like a failure. It only renders once the vote
                counts are actually loaded. */}
            {pollSkipped && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 border-muted-foreground/40 text-muted-foreground whitespace-nowrap"
                title="Nobody voted in this poll and the match finished over a day ago, so it's treated as skipped and left out of team analytics."
              >
                Voting skipped
              </Badge>
            )}
            {showPastKickoff && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0 border-amber-500/60 text-amber-600 dark:text-amber-400"
                title="This poll is still open but the match date has passed. Close it to keep your active count accurate."
              >
                Past kickoff
              </Badge>
            )}
            </div>
          )}

          {!gameIsBye && (
            <label className="inline-flex items-center gap-1.5 select-none">
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Voting</span>
              <Switch
                checked={game.voting_open}
                onCheckedChange={handleToggle}
                disabled={!onToggleVoting}
                aria-label={game.voting_open ? "Close voting" : "Open voting"}
              />
              <span
                aria-hidden
                className={cn(
                  "text-xs font-medium w-[42px]",
                  game.voting_open ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
                )}
              >
                {game.voting_open ? "Open" : "Closed"}
              </span>
            </label>
          )}
        </div>
      </div>
      
      <h3 className="relative z-10 text-xl font-semibold mb-1">
        {gameIsBye ? "Bye — no game" : `vs. ${game.opponent}`}
      </h3>


      {(game.match_location || game.match_field) && (
        <p className="relative z-10 text-xs text-muted-foreground mb-1 flex items-center gap-1">
          <MapPin className="h-3 w-3" />
          {[game.match_location, game.match_field].filter(Boolean).join(" · ")}
        </p>
      )}
      
      {gameIsBye ? (
        <p className="relative z-10 text-sm text-muted-foreground mb-2">
          No fixture this round. Nothing to organise.
        </p>
      ) : (
        <div className="relative z-10 flex items-center gap-3 text-sm text-muted-foreground mb-2">
          <span className="flex items-center gap-1">
            <Users className="h-4 w-4" />
            {voteCounts} votes
          </span>
        </div>
      )}

      {!gameIsBye && rsvpData && rsvpData.total > 0 && (

        <Link to={`/rsvp/${game.id}`} className="block relative z-10 mb-4 hover:opacity-80 transition-opacity cursor-pointer">
          <div className="relative h-2 w-full rounded-full overflow-hidden bg-muted">
            <div className="flex h-full w-full">
              {rsvpData.going > 0 && (
                <div
                  className="bg-green-500 transition-all"
                  style={{ width: `${(rsvpData.going / rsvpData.total) * 100}%` }}
                />
              )}
              {rsvpData.notGoing > 0 && (
                <div
                  className="bg-red-400 transition-all"
                  style={{ width: `${(rsvpData.notGoing / rsvpData.total) * 100}%` }}
                />
              )}
            </div>
            {playersPerSide && (
              <div
                className={`absolute top-[-2px] bottom-[-2px] w-0.5 rounded-sm ${rsvpData.going >= playersPerSide ? 'bg-green-600' : 'bg-foreground/70'}`}
                style={{ left: `${Math.min(100, (playersPerSide / rsvpData.total) * 100)}%` }}
                title={`Target: ${playersPerSide} on field`}
                aria-label={`Target: ${playersPerSide} on field`}
              />
            )}
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              {rsvpData.going} ✓
            </span>
            <span className="flex items-center gap-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-red-400" />
              {rsvpData.notGoing} ✗
            </span>
            <span className="flex items-center gap-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/30" />
              {rsvpData.total - rsvpData.going - rsvpData.notGoing} ?
            </span>
          </div>
        </Link>
      )}

      {(() => {
        const kickoff = new Date(game.date);
        if (game.match_time) {
          const [h, m] = String(game.match_time).split(":").map(Number);
          kickoff.setHours(h || 0, m || 0, 0, 0);
        } else {
          kickoff.setHours(23, 59, 59, 999);
        }
        const isPast = kickoff < new Date();
        if (!isPast || !game.team_id) return null;
        return (
          <Link
            to={`/teams/${game.team_id}/attendance/match/${game.id}`}
            className="relative z-10 -mt-2 mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ClipboardCheck className="h-3.5 w-3.5" />
            Review attendance
          </Link>
        );
      })()}

      {onToggleVoting ? (
        <div className="relative z-10 flex flex-col gap-2 mt-auto pb-3">
          {!gameIsBye && (<>
          <Button

            variant="default"
            size="sm"
            className="gap-1 w-full"
            asChild
          >
            <Link to={`/game/${game.id}?view=results`}>
              <BarChart className="h-4 w-4" />
              <span>Results</span>
            </Link>
          </Button>

          <div className="flex flex-col gap-2 mt-1 pe-1">
            <div className="flex items-stretch gap-1.5 min-w-0">
              {game.rsvp_enabled ? (
                <LinkCopyButton
                  to={`/rsvp/${game.id}`}
                  onCopy={handleCopyRsvpUrl}
                  copied={rsvpCopied}
                  label="📋 RSVP"
                  copyAriaLabel="Copy RSVP link"
                  navAriaLabel="Open RSVP page"
                />
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  className="gap-1.5 text-xs justify-start h-11 flex-1"
                  asChild
                >
                  <Link to={`/game/${game.id}?view=results`}>
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Manage Poll</span>
                  </Link>
                </Button>
              )}
              {game.rsvp_enabled && (
                <ShareIntentButton
                  platform={sharePrefs.share_platform}
                  text={buildRsvpShareText(game, ogRsvpUrl(game.id), teamName)}
                  url={ogRsvpUrl(game.id)}
                  groupUrl={sharePrefs.share_group_url}
                  ariaLabel="Send RSVP link to your team chat"
                />
              )}
              {game.rsvp_enabled && sharePrefs.telegram_chat_id && (
                <SendToTelegramButton
                  teamId={game.team_id}
                  text={buildRsvpShareText(game, ogRsvpUrl(game.id), teamName)}
                  ariaLabel="Post RSVP link to your Telegram group"
                />
              )}
            </div>

            <div className="flex items-stretch gap-1.5 min-w-0">
              <LinkCopyButton
                to={`/game/${game.id}?view=coach`}
                onCopy={handleCopyPollUrl}
                copied={pollCopied}
                label="🗳️ MVP Vote"
                copyAriaLabel="Copy MVP Vote link"
                navAriaLabel="Open MVP vote page"
              />
              <ShareIntentButton
                platform={sharePrefs.share_platform}
                text={buildMvpShareText(game, ogGameUrl(game.id), teamName)}
                url={ogGameUrl(game.id)}
                groupUrl={sharePrefs.share_group_url}
                ariaLabel="Send MVP Vote link to your team chat"
              />
              {sharePrefs.telegram_chat_id && (
                <SendToTelegramButton
                  teamId={game.team_id}
                  text={buildMvpShareText(game, ogGameUrl(game.id), teamName)}
                  ariaLabel="Post MVP Vote link to your Telegram group"
                />
              )}
            </div>
          </div>
          </>)}


          <div className="grid grid-cols-3 gap-2 mt-1">
            <EditGameDialog game={game} onSaved={() => onEditGame?.()}>
              <Button variant="outline" size="sm" className="gap-1">
                <Pencil className="h-4 w-4" />
                <span>Edit</span>
              </Button>
            </EditGameDialog>
            {onPinGame && (
              <Button 
                variant={isPinned ? "secondary" : "outline"}
                size="sm"
                className="gap-1"
                onClick={() => onPinGame(game.id)}
              >
                {isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                <span>{isPinned ? "Unpin" : "Pin to URL"}</span>
              </Button>
            )}
            <Button 
              variant="destructive"
              size="sm"
              className="gap-1"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </Button>
          </div>

          {game.rsvp_enabled && isAdmin && (
            <NudgePendingButton gameId={game.id} className="mt-1" />
          )}

          <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this match?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-3">
                    <p>
                      You're about to delete <span className="font-medium">vs. {game.opponent}</span> ({formattedDate}).
                    </p>
                    {impactLoading ? (
                      <p className="text-sm text-muted-foreground">Checking linked data…</p>
                    ) : impact && (impact.votes + impact.feedback + impact.rsvps) > 0 ? (
                      <div className="text-sm">
                        <p className="mb-1">This match has linked data:</p>
                        <ul className="list-disc ps-5 space-y-0.5">
                          {impact.votes > 0 && <li>{impact.votes} MVP vote{impact.votes === 1 ? "" : "s"}</li>}
                          {impact.feedback > 0 && <li>{impact.feedback} coach feedback response{impact.feedback === 1 ? "" : "s"}</li>}
                          {impact.rsvps > 0 && <li>{impact.rsvps} RSVP{impact.rsvps === 1 ? "" : "s"}</li>}
                        </ul>
                        <p className="mt-2 text-muted-foreground">
                          Votes and feedback only exist in the context of a match, so they'll be removed with it. There's no way to keep them detached.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No votes, feedback or RSVPs are attached to this match.</p>
                    )}
                    <p className="text-sm">This action cannot be undone.</p>
                    {!gameIsBye && (
                      <p className="text-sm text-muted-foreground">
                        If there's simply no fixture this round, mark it as a bye instead — it stays on the schedule but nobody is asked to RSVP.
                      </p>
                    )}
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
                {!gameIsBye && (
                  <Button
                    variant="outline"
                    onClick={handleMarkBye}
                    disabled={isDeleting || markingBye}
                  >
                    {markingBye ? "Saving…" : "Mark as bye instead"}
                  </Button>
                )}
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={isDeleting || impactLoading || markingBye}
                >
                  {isDeleting ? "Deleting..." : "Delete match and data"}
                </AlertDialogAction>
              </AlertDialogFooter>

            </AlertDialogContent>
          </AlertDialog>

        </div>
      ) : (
        <div className="mt-auto pb-3">
          <Button 
            variant="default" 
            size="sm"
            className="w-full gap-1"
            asChild
          >
            <Link to={`/game/${game.id}`}>
              <span>Vote Now</span>
              <ExternalLink className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      )}
    </GlassCard>
  );
}
